<template>
  <DeckCaptionsContainer
    class="no-transition cursor-text hover_opacity-100 focus-within_opacity-100"
    :class="caption ? 'opacity-90' : 'opacity-70'"
    :show="show"
    :caption="caption"
    @click="clickHandler"
  >
    <InlineEditable
      ref="inputRef"
      v-model:value="caption"
      allow-line-break
      class="enhance-placeholders"
      theme="dark"
      :maxlength="2048"
      :placeholder="'Insert Caption…'"
      @save="updateCaption"
      @clear="updateCaption"
      @cancel="!isSaving && (caption = (slide && slide.description) || '')"
    />
  </DeckCaptionsContainer>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, toRefs, watch } from 'vue'

import type { Slide } from '@/entities/deck'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide> | null>,
    default: null,
  },
  show: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['save'])

const { show, slide } = toRefs(props)

const caption = ref(props.slide?.description ?? '')
const inputRef = ref()
const isSaving = ref(false)

// update caption when slide changes
watch(
  [slide, show],
  async ([slide, show]) => {
    if (!show) return
    // On firefox and safari in some cases description can be \n
    caption.value = slide?.description && slide.description.trim() !== '' ? slide.description : ''
    isSaving.value = false
  },
  { immediate: true }
)

async function updateCaption() {
  if (props.slide?.description !== caption.value) {
    isSaving.value = true
    emit('save', { id: slide.value?.id, description: caption.value })
  }
}

// This is so we can click the container around the text to start an edit
function clickHandler(event: MouseEvent) {
  inputRef.value.$el.dispatchEvent(new Event('click', event))
}
</script>

<style lang="stylus" scoped>
:deep(.ui-inline-editable.dark)
  @apply border-none p-0 /* we have padding on the CaptionContainer already */
  &:hover,
  &:focus-within
    @apply border-none bg-transparent
</style>
